<template>
  <div class="row">
    <div class="col-12">
      <card>
        <el-collapse v-model="activeName" @change="handleCollapse">
          <el-collapse-item :title="titleFilter" name="filter">
            <filter-component
              @filter="handleFilter"
              @showHideLoading="showHideLoading"
              :filterOfLots="false"
              :filterOfFarms="false"
            >
              <template slot="prev">
                <select-component
                  class="pt-3"
                  :rowClass="`col-12 col-sm-12 col-md-6 col-lg-6 col-xl-2`"
                  :labelSelect="`${$t('production.report_type')}: *`"
                  :isMultiple="false"
                  :dataOptions="reportType"
                  :initialSelection="dataFilter.reportType"
                  @onChange="handleChangeSelected"
                  modelName="reportType"
                />
                <select-component
                  class="pt-3"
                  :rowClass="`col-12 col-sm-12 col-md-6 col-lg-6 col-xl-4`"
                  :labelSelect="`${$t('common_words.farm')}: *`"
                  :requireClass="`required-farmId`"
                  :isMultiple="false"
                  :dataOptions="farms"
                  :initialSelection="dataFilter.farmId"
                  @onChange="handleChangeSelected"
                  modelName="farmId"
                />
              </template>
              <template slot="after-lot" v-if="dataFilter.reportType == 2">
                <select-component
                  class="pt-3"
                  :rowClass="`col-12 col-sm-12 col-md-6 col-lg-6 col-xl-3`"
                  :labelSelect="`${$t('production.station')}: *`"
                  :requireClass="`required-stationId`"
                  :isMultiple="false"
                  :dataOptions="stations"
                  :initialSelection="dataFilter.stationId"
                  @onChange="handleChangeSelected"
                  modelName="stationId"
                />
                <select-component
                  class="pt-3"
                  :rowClass="`col-12 col-sm-12 col-md-6 col-lg-6 col-xl-3`"
                  :labelSelect="`${$t('production.sensor')}: *`"
                  :requireClass="`required-sensors`"
                  :dataOptions="sensors"
                  :initialSelection="dataFilter.sensors"
                  @onChange="handleChangeSelected"
                  modelName="sensors"
                />
              </template>
              <template slot="after-lot" v-if="dataFilter.reportType == 5">
                <p class="d-none d-xl-block col-xl-6"></p>
                <fg-input
                  type="number"
                  class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 mt-2"
                  :label="`${$t('production.subgrade_limit')}: *`"
                  v-model="dataFilter.subDegreeLimit"
                  name="production.subgrade_limit"
                  v-validate="
                    `required|integer|min_value:20|max_value:${computedSubGradeLimitMax}`
                  "
                  :error="getError('production.subgrade_limit', true)"
                />
                <fg-input
                  type="number"
                  class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 mt-2"
                  :label="`${$t('production.lower_degree_limit')}: *`"
                  v-model="dataFilter.lowerDegreeLimit"
                  name="production.lower_degree_limit"
                  v-validate="
                    `required|integer|min_value:${computedLowerDegreeLimitMin}|max_value:${computedLowerDegreeLimitMax}`
                  "
                  :error="getError('production.lower_degree_limit', true)"
                />
                <fg-input
                  type="number"
                  class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 mt-2"
                  :label="`${$t('production.upper_degree_limit')}: *`"
                  v-model="dataFilter.upperDegreeLimit"
                  name="production.upper_degree_limit"
                  v-validate="
                    `required|integer|min_value:${computedUpperDegreeLimitMin}|max_value:${computedUpperDegreeLimitMax}`
                  "
                  :error="getError('production.upper_degree_limit', true)"
                />
                <fg-input
                  type="number"
                  class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 mt-2"
                  :label="`${$t('production.overgrade_limit')}: *`"
                  v-model="dataFilter.overDegreeLimit"
                  name="production.overgrade_limit"
                  v-validate="
                    `required|integer|min_value:${computedOverDegreeLimitMin}|max_value:60`
                  "
                  :error="getError('production.overgrade_limit', true)"
                />
              </template>
            </filter-component>
          </el-collapse-item>
        </el-collapse>
      </card>
      <card v-if="validateData">
        <el-container>
          <el-main>
            <div class="row">
              <div class="col-12">
                <card class="custom-shadow-card-header" v-if="chart">
                  <div class="row">
                    <chart-header-options
                      :chartData="actualChart"
                      :autoChange="false"
                    >
                      <template
                        slot="append"
                        v-if="
                          dataFilter.reportType == 2 && editBaseTempThreshold()
                        "
                      >
                        <div
                          class="col-1"
                          style="width: 25px; flex: initial !important"
                          :v-tooltip.bottom="`${$t(
                            'climate.base_temperature_upper_threshold'
                          )}`"
                          v-if="dataFilter.reportType == 2"
                        >
                          <temp-base-form
                            :minTemperature="getPropMinTempForm"
                            :maxTemperature="getPropMaxTempForm"
                            :isFahrenheit="data.fahrenheit"
                            @getDataReport="getData"
                          />
                        </div>
                      </template>
                    </chart-header-options>
                    <div
                      class="col-1"
                      style="padding-top: 9px; padding-right: 27px"
                      v-if="dataFilter.reportType == 2"
                    >
                      <info-temp />
                    </div>
                    <div class="col-12">
                      <highcharts :options="actualChart"></highcharts>
                    </div>
                  </div>
                </card>
                <card class="custom-shadow-card-header" v-if="tableData">
                  <card-header
                    :propPagination="pagination"
                    @searchedQuery="searchedQuery"
                    class="mr-3 mt-4"
                  />
                  <master-table
                    :infoTable="queriedData"
                    :columns="tableColumns"
                  />
                  <card-footer ref="cardfooter" :propPagination="pagination" />
                  <export-tables
                    :pdf="{
                      filename: `${$t('production.production_vs_climate')}.pdf`,
                      route: 'summary/productionvsclimate/pdf',
                      paramMethod: 'POST',
                      plusData: { ...exportData },
                    }"
                    :xls="{
                      filename: `${$t(
                        'production.production_vs_climate'
                      )}.xlsx`,
                      route: 'summary/productionvsclimate/excel',
                      paramMethod: 'POST',
                      plusData: { ...exportData },
                    }"
                  />
                  <br />
                </card>
              </div>
            </div>
          </el-main>
        </el-container>
      </card>
    </div>
  </div>
</template>
<script>
import { Axios } from 'src/class/Axios.js';
import { Chart } from 'highcharts-vue';
import { isset } from 'src/util/Util.js';
import axios from 'axios';
import ChartHeaderOptions from 'src/components/Chart/ChartHeaderOptions.vue';
import configTable from './ReportTable';
import Filter from 'src/components/Filter/Filters.vue';
import i18n from 'src/i18n';
import InfoTemp from './InfoTemp.vue';
import MixinCharts from 'src/util/mixinCharts';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinMasters from 'src/util/mixinMasters';
import MixinMenu from 'src/util/mixinMenu';
import MixinPermissions from 'src/util/mixinPermissions';
import SweetAlert from 'src/class/Alerts';
import TableDetails from './TableDetails.vue';
import TempBaseForm from './TempBaseForm.vue';

export default {
  components: {
    [ChartHeaderOptions.name]: ChartHeaderOptions,
    [Filter.name]: Filter,
    [InfoTemp.name]: InfoTemp,
    [TableDetails.name]: TableDetails,
    [TempBaseForm.name]: TempBaseForm,
    highcharts: Chart,
  },

  computed: {
    ...configTable().Search,
    exportData() {
      const response = {
        data: { ...this.tableData },
        type: this.dataFilter.reportType,
      };
      delete response.route;

      return response;
    },
    titleFilter() {
      if (this.valueCollapse) {
        return this.valueCollapse.length === 1
          ? this.$i18n.t('uti.mixinFilter.hideFilters')
          : this.$i18n.t('uti.mixinFilter.showFilters');
      }

      return this.$i18n.t('uti.mixinFilter.hideFilters');
    },
    reportType() {
      return [
        { id: 1, name: 'Calibración' },
        // { id: 2, name: 'Embolse' },
        { id: 3, name: 'Cosecha' },
        { id: 4, name: 'Cajas Elaboradas' },
        { id: 5, name: 'Racimos' },
      ];
    },
    validateData() {
      return this.chart || this.tableData;
    },
    getPropMinTempForm() {
      return {
        value: this.data.baseTemperature,
        label: i18n.t('climate.base_temperature'),
      };
    },
    getPropMaxTempForm() {
      return {
        value: this.data.upperThreshold,
        label: i18n.t('climate.upper_threshold'),
      };
    },
    computedSubGradeLimitMax() {
      return this.dataFilter.lowerDegreeLimit
        ? Number(this.dataFilter.lowerDegreeLimit) - 1
        : null;
    },
    computedLowerDegreeLimitMin() {
      return this.dataFilter.subDegreeLimit
        ? Number(this.dataFilter.subDegreeLimit) + 1
        : null;
    },
    computedLowerDegreeLimitMax() {
      return this.dataFilter.upperDegreeLimit
        ? Number(this.dataFilter.upperDegreeLimit) - 1
        : null;
    },
    computedUpperDegreeLimitMin() {
      return this.dataFilter.lowerDegreeLimit
        ? Number(this.dataFilter.lowerDegreeLimit) + 1
        : null;
    },
    computedUpperDegreeLimitMax() {
      return this.dataFilter.overDegreeLimit
        ? Number(this.dataFilter.overDegreeLimit) - 1
        : null;
    },
    computedOverDegreeLimitMin() {
      return this.dataFilter.upperDegreeLimit
        ? Number(this.dataFilter.upperDegreeLimit) + 1
        : null;
    },
  },

  data() {
    return {
      pagination: {},
      tableColumns: [],
      valueCollapse: null,
      activeName: 'filter',
      stations: [],
      farms: [],
      sensors: [],
      dataFilter: {
        farmId: null,
        stationId: null,
        sensors: [],
        reportType: 1,
        comparate: false,
        overDegreeLimit: 52,
        upperDegreeLimit: 48,
        lowerDegreeLimit: 39,
        subDegreeLimit: 38,
      },
      data: [],
      actualChart: {},
      chartOptions: [],
      chart: null,
      tableData: null,
    };
  },

  mixins: [MixinMasters, MixinMenu, MixinForm, MixinPermissions, MixinCharts],

  watch: {
    '$i18n.locale': 'setupTable',
  },

  methods: {
    handleChangeSelected(data) {
      this.$set(this.dataFilter, data.name, data.selects);
      if (data.name == 'stationId') {
        this.getSensors();
      }
    },

    handleFilter(data) {
      if (this.isFilterValid()) {
        this.dataFilter = { ...this.dataFilter, ...data };

        if (this.dataFilter.reportType === 5) {
          this.$validator.validateAll().then((isValid) => {
            if (isValid) {
              this.getData();
            } else {
              SweetAlert.warning(i18n.t('production.check_degree_limits'));
            }
          });
        } else {
          this.getData();
        }
      }
    },

    isFilterValid() {
      if (!this.dataFilter.farmId) {
        this.validationInputsRequired('farmId');
        return false;
      }

      if (this.dataFilter.reportType === 2) {
        if (!this.dataFilter.stationId) {
          this.validationInputsRequired('stationId');
          return false;
        }
        if (
          !Array.isArray(this.dataFilter.sensors) ||
          this.dataFilter.sensors.length === 0
        ) {
          this.validationInputsRequired('sensors');
          return false;
        }
      }

      return true;
    },

    handleCollapse(val) {
      this.valueCollapse = val;
    },

    handleView(_, row) {
      this.rowData = {
        ...row,
      };
      this.modals['read'] = true;
    },

    getPostData() {
      switch (this.dataFilter.reportType) {
        case 1:
        case 3:
        case 4:
        case 5:
          return {
            ...this.dataFilter,
            route: 'summary/productionvsclimate',
          };

        case 2:
          return {
            ...this.dataFilter,
            route: 'climates/accumulator',
            stations: [this.dataFilter.stationId],
            sensors: this.dataFilter.sensors,
            report: 1,
            cropId: 1,
          };

        default:
          break;
      }
    },

    getData() {
      this.showHideLoading(true);
      this.setupTable();

      let data = this.getPostData();

      if (data.bySel === 'Año') {
        data.datei += '-01-01';
        data.datef += '-12-31';
      }

      Axios.postMasters(data)
        .then((response) => {
          switch (this.dataFilter.reportType) {
            case 1:
            case 3:
            case 4:
            case 5:
              this.tableData = response.data.data.table;
              this.chart = response.data.data.chart;

              if (this.chart) {
                this.actualChart = {};
                setTimeout(() => {
                  this.actualChart = { ...this.columns(this.chart) };
                }, 0.1);
              } else {
                SweetAlert.warning(i18n.t('common_words.unavailable_data'));
              }
              break;
            case 2:
              this.tableData =
                response.data.data[this.dataFilter.stationId].table;
              this.chart =
                response.data.data[this.dataFilter.stationId].hcAirChart;

              if (this.chart) {
                this.actualChart = {};
                setTimeout(() => {
                  this.actualChart = {
                    ...this.lines(this.chart, false, 500, false),
                  };
                }, 0.1);
              } else {
                SweetAlert.warning(i18n.t('common_words.unavailable_data'));
              }
              break;

            default:
              break;
          }
          this.pagination = { ...Table.pagination };
          this.showHideLoading();
        })
        .catch((error) => {
          this.showHideLoading();
          SweetAlert.warning(error.response.data.message);
        });
    },

    setupTable() {
      const { Table } = configTable();
      switch (this.dataFilter.reportType) {
        case 1:
          this.tableColumns = Table.calibrationTable;
          break;

        case 2:
          this.tableColumns = Table.heatColumnTable;
          break;

        case 3:
          this.tableColumns = Table.cuttingTable;
          break;

        case 4:
          this.tableColumns = Table.boxesTable;
          break;

        case 5:
          this.tableColumns = Table.clustersTable;
          break;

        default:
          break;
      }
      this.pagination.perPage = 10;
      this.pagination.currentPage = 1;
      this.searchQuery = '';
    },

    getSensors() {
      this.dataFilter.sensors = [];
      axios
        .get(`/sensorsPerStation/${this.dataFilter.stationId}`)
        .then((response) => {
          this.sensors = this.concatAll(response.data);
        });
    },
  },

  created() {
    this.setupTable();
    Promise.all([
      Axios.getMasters('devices/stations'),
      Axios.getMasters('farms/farms/only'),
    ])
      .then(([stationsResponse, farmsResponse]) => {
        this.stations = stationsResponse.data.data;
        this.farms = farmsResponse.data.data;
      })
      .catch((error) => this.showMessageErrorsSwalAlert(error));
  },

  mounted() {
    this.selectedItemOnMenu('summary');
  },

  destroyed() {
    this.deselectedItemOnMenu('summary');
  },
};
</script>
